export interface IQueryRangeItem {
  gt?: unknown;
  gte?: unknown;
  lt?: unknown;
  lte?: unknown;
  ne?: unknown;
}

export interface IQueryTerms<T> {
  terms(field: keyof T, items?: unknown | unknown[]): Record<string, unknown> | undefined;
  term(field: keyof T, item?: unknown): Record<string, unknown> | undefined;
  nested(path: keyof T, fn: INestedQueryFn<T>): Record<string, unknown>;
  script(item: string): Record<string, unknown> | undefined;
  match_bool_prefix(field: keyof T, item?: unknown): Record<string, unknown> | undefined;
  multi_match(field: keyof T, item?: unknown, type?: { type: string }): Record<string, unknown> | undefined;
  range(field: keyof T, ...items: IQueryRangeItem[]): Record<string, unknown> | undefined;
  between(field: keyof T, lower?: unknown, upper?: unknown): Record<string, unknown> | undefined;
  intersects(lowerField: keyof T, upperfield: keyof T, lower?: unknown, upper?: unknown): Record<string, unknown> | undefined;
  contains(lowerField: keyof T, upperfield: keyof T, value: unknown): Record<string, unknown> | undefined;
  raw(value: Record<string, unknown>): Record<string, unknown> | undefined;
  bool(fn: IBoolFn<T>): Record<string, unknown>;
}

export interface IBoolQueryClause<T> {
  should(fn: IQueryFn<T>): IBoolQueryClause<T>;
  must(fn: IQueryFn<T>): IBoolQueryClause<T>;
  must_not(fn: IQueryFn<T>): IBoolQueryClause<T>;
  filter(fn: IQueryFn<T>): IBoolQueryClause<T>;
  minimum_should_match(n: number): IBoolQueryClause<T>;
  boost(n: number): IBoolQueryClause<T>;
}

type IQueryFn<T> = (fn: IQueryTerms<T>) => (Record<string, unknown> | undefined)[];
type INestedQueryFn<T> = (fn: IQueryTerms<T>) => Record<string, unknown>;
type IBoolFn<T> = (fn: IBoolQueryClause<T>) => IBoolQueryClause<T>;
interface IIndex {
  _index: string;
}

export function Q<T, R = T & IIndex>(): IQueryTerms<R> {
  return {
    range(field: keyof R, ...items: IQueryRangeItem[]): Record<string, unknown> | undefined {
      const x = items?.filter((i) => !!i && (i.gt ?? i.gte ?? i.lt ?? i.lte ?? i.ne ?? false));
      if (!x || x.length === 0) return undefined;
      return {
        range: {
          [field]: Object.assign.apply(null, [{}, ...x]),
        },
      };
    },

    raw(value: Record<string, unknown>): Record<string, unknown> | undefined {
      return value;
    },

    script(item: string): Record<string, unknown> | undefined {
      if (!item) return undefined;
      return {
        script: {
          script: item,
        },
      };
    },

    between(field: keyof R, lower?: unknown, upper?: unknown): Record<string, unknown> | undefined {
      if (!lower && !upper) return undefined;
      return {
        range: {
          [field]: { gte: lower, lte: upper },
        },
      };
    },

    intersects(lowerField: keyof R, upperField: keyof R, lower?: unknown, upper?: unknown): Record<string, unknown> | undefined {
      if (!lowerField || !upperField || (!lower && !upper)) return undefined;
      return {
        bool: {
          must: [
            {
              range: {
                [lowerField]: { lt: upper },
              },
            },
            {
              range: {
                [upperField]: { gt: lower },
              },
            },
          ],
        },
      };
    },

    contains(lowerField: keyof R, upperField: keyof R, value: unknown): Record<string, unknown> | undefined {
      if (!lowerField || !upperField || !value) return undefined;
      return {
        bool: {
          must: [
            {
              range: {
                [lowerField]: { lte: value },
              },
            },
            {
              range: {
                [upperField]: { gte: value },
              },
            },
          ],
        },
      };
    },

    terms(field: keyof R, items?: unknown | unknown[]): Record<string, unknown> | undefined {
      if (!items) return undefined;
      return {
        terms: {
          [field]: Array.isArray(items) ? items : [items],
        },
      };
    },

    nested(field: keyof R, fn: INestedQueryFn<R>): Record<string, unknown> {
      const query = fn(Q<R>());
      return {
        nested: {
          path: field,
          query,
        },
      };
    },

    term(field: keyof R, item?: unknown): Record<string, unknown> | undefined {
      if (item == null) return undefined;
      return {
        term: {
          [field]: item,
        },
      };
    },

    multi_match(_field: keyof R, item?: unknown, type: { type: string } = { type: 'bool_prefix' }) {
      if (item == null) return undefined;
      const field: string = _field as string;
      return {
        multi_match: {
          query: item,
          ...type,
          fields: [field, `${field}._3gram`, `${field}._2gram`],
        },
      };
    },

    match_bool_prefix(field: keyof R, item?: unknown): Record<string, unknown> | undefined {
      if (item == null) return undefined;
      return {
        match_bool_prefix: {
          [field]: item,
        },
      };
    },

    bool(boolFn: IBoolFn<R>): Record<string, unknown> {
      const value: Record<string, unknown> = {};
      function _filter(op: 'filter' | 'should' | 'must' | 'must_not', fn: IQueryFn<R>) {
        const filters = fn(Q<R>())?.filter((m) => !!m && Object.keys(m).length > 0);
        if (filters.length === 0) return undefined;
        const x: Record<string, any> = value.bool ?? (value.bool = {});
        x[op] = filters;
      }

      const bool_query: IBoolQueryClause<R> = {
        should(fn: IQueryFn<R>) {
          _filter('should', fn);
          return bool_query;
        },
        must(fn: IQueryFn<R>) {
          _filter('must', fn);
          return bool_query;
        },
        must_not(fn: IQueryFn<R>) {
          _filter('must_not', fn);
          return bool_query;
        },
        filter(fn: IQueryFn<R>) {
          _filter('filter', fn);
          return bool_query;
        },
        minimum_should_match(n: number) {
          const x: Record<string, any> = value.bool ?? (value.bool = {});
          x.minimum_should_match = n;
          return bool_query;
        },
        boost(n: number) {
          const x: Record<string, any> = value.bool ?? (value.bool = {});
          x.boost = n;
          return bool_query;
        },
      };
      boolFn(bool_query);
      return value;
    },
  };
}
